/**
 * Per-camera metadata event bus.
 *
 * PlayerConnection publishes each parsed metadata packet of the data channel here. The
 * video overlays (OverlayCanvas, MotionMaskOverlay), the archive unlock prompt and the
 * notifications panel subscribe by camera id. Thus, they do not need a second WebRTC
 * session or a global store.
 */

import type { Unsubscribe } from '@/types/common';
import type { MetadataPacket } from '@/types/metadataPacket';

type MetadataSubscriber = (packet: MetadataPacket) => void;

const subs = new Map<string, Set<MetadataSubscriber>>();

export function subscribeMetadata(
    cameraId: string | undefined, fn: MetadataSubscriber,
): Unsubscribe {
    if (!cameraId) {
        return () => {};
    }
    let set = subs.get(cameraId);
    if (!set) {
        set = new Set();
        subs.set(cameraId, set);
    }
    set.add(fn);
    return () => {
        const subscribers = subs.get(cameraId);
        if (!subscribers) {
            return;
        }
        subscribers.delete(fn);
        if (subscribers.size === 0) {
            subs.delete(cameraId);
        }
    };
}

export function publishMetadata(cameraId: string, packet: MetadataPacket): void {
    const set = subs.get(cameraId);
    if (!set) {
        return;
    }
    for (const fn of set) {
        try { fn(packet); } catch { /* ignore subscriber errors */ }
    }
}
